import React, { useState, useEffect, useContext, createContext } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { axiosInstance } from '../utils/tokenUtils';

const AuthContext = createContext(undefined);

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState({
    user_type: null,
    access_token: '',
    id: null,
    first_name: null,
    last_name: null,
    email: null,
    profile_image: null,
  });
  const [loading, setLoading] = useState(true);

  // Function to refresh auth data from API
  const refreshAuth = async () => {
    try {
      const { data } = await axiosInstance.get('/auth/users/me');
      setAuth(prevAuth => ({
        ...prevAuth,
        id: data?.id ?? prevAuth.id,
        user_type: data?.user_type ?? prevAuth.user_type,
        first_name: data?.first_name ?? prevAuth.first_name,
        last_name: data?.last_name ?? prevAuth.last_name,
        email: data?.email ?? prevAuth.email,
        profile_image: data?.profile_image ?? prevAuth.profile_image,
      }));
    } catch (err) {
      // On failure, keep existing auth state
      // Optionally fall back to cookies for non-critical fields
      const first_name = Cookies.get('first_name');
      const last_name = Cookies.get('last_name');
      const email = Cookies.get('email');
      const user_type = Cookies.get('user_type');
      const profile_image = Cookies.get('profile_image');
      setAuth(prevAuth => ({
        ...prevAuth,
        user_type: user_type ?? prevAuth.user_type,
        first_name: first_name ?? prevAuth.first_name,
        last_name: last_name ?? prevAuth.last_name,
        email: email ?? prevAuth.email,
        profile_image: profile_image ?? prevAuth.profile_image,
      }));
    }
  };

  // Set up axios header whenever auth changes
  useEffect(() => {
    if (auth.access_token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${auth.access_token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [auth.access_token]);

  // Initialize auth: get token from cookies, then fetch profile from API
  useEffect(() => {
    const initialize = async () => {
      const access_token = Cookies.get('access_token');
      // Seed token so requests can authenticate
      if (access_token) {
        setAuth(prevAuth => ({
          ...prevAuth,
          access_token,
        }));
      }

      await refreshAuth();
      setLoading(false);
    };

    initialize();
  }, []);

  return (
    <AuthContext.Provider value={[auth, setAuth, loading, refreshAuth]}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { useAuth, AuthProvider };
